import { useEffect } from 'react';
import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface ChangelogPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

type TagKind = 'feature' | 'improvement' | 'fix' | 'change';

interface ChangelogEntry {
  version: string;
  date: string;
  headline: string;
  body: string;
  tags: { label: string; kind: TagKind }[];
  items: string[];
}

const ENTRIES: ChangelogEntry[] = [
  {
    version: 'v1.0.15',
    date: 'Jun 2026',
    headline: 'Follow-up radar + win-rate play badges',
    body: 'Two new signals to help you act on what the data already knows. The follow-up radar surfaces leads that have gone quiet for three or more days — sorted by deal-state urgency — right on the extension welcome screen and as a live icon badge. Win-rate badges show your personal reply rate for each draft play, so you can see at a glance which angle has historically worked for you.',
    tags: [{ label: 'Feature', kind: 'feature' }],
    items: [
      'Going-cold leads surface on the welcome screen sorted by deal-state priority (Closing first)',
      'Extension icon badge shows a live count of stale conversations',
      'Win-rate badge on each draft card: "★ 62% replied for you" once you have 3+ sends for that play',
      'Star marks your highest-performing play in each generated set',
      'Confidence floor — no rate shown below 3 sends to avoid misleading sparse data',
    ],
  },
  {
    version: 'v1.0.14',
    date: 'Jun 2026',
    headline: 'Drafts grounded against thread facts + inline suggestion tracking',
    body: 'Drafts now pull from two additional grounding sources before Claude writes: your personal knowledge base (business facts and tone examples extracted from your sent replies) and anonymized industry-wide play performance data. Inline suggestion results are now persisted so accept rates can be tracked over time.',
    tags: [{ label: 'Feature', kind: 'feature' }],
    items: [
      'Drafts draw on your personal knowledge base for durable facts (pricing, hours, plan tiers)',
      'Tone examples from your sent replies inform Claude\'s writing style',
      'Industry-cohort winning-play data fed into draft prompt for better angle selection',
      'Inline suggestion batches persisted to database with accept-index tracking',
      'record_inline_acceptance RPC records which suggestions you acted on',
    ],
  },
  {
    version: 'v1.0.13',
    date: 'Jun 2026',
    headline: 'Fewer generic drafts — less name overuse, sharper inline catches',
    body: 'Tuning pass on both the draft engine and the inline suggestion engine. Drafts are more tightly anchored to the actual thread content and less likely to fall back on generic frameworks. Lead-name overuse in short-form and SMS drafts is reduced, and a creative-angle directive steers Claude away from formulaic openers.',
    tags: [{ label: 'Improvement', kind: 'improvement' }],
    items: [
      'Draft engine prompt updated to anchor copy more tightly to the thread content',
      'Inline suggestion model updated with tighter phrase-level constraints',
      'Reduced over-use of lead name in SMS and short-form drafts',
      'Creative angle directive added to avoid formulaic opener patterns',
    ],
  },
  {
    version: 'v1.0.12',
    date: 'May 2026',
    headline: 'Internal — no user-visible changes',
    body: 'Version bump and internal dependency updates. No new features, no behavior changes.',
    tags: [{ label: 'Change', kind: 'change' }],
    items: [],
  },
  {
    version: 'v1.0.11',
    date: 'May 2026',
    headline: 'Streamlined UI — draft refinement panel removed',
    body: 'The draft refinement panel has been removed from the extension side panel. The refine-draft edge function remains available on Basic and above plans, but the UI surface has been simplified to keep the flow focused on generation and injection.',
    tags: [{ label: 'Change', kind: 'change' }],
    items: [
      'Draft refinement panel removed from extension side panel UI',
      'Core generate → review → inject flow is now the primary path',
      'No changes to the underlying refine-draft edge function',
    ],
  },
  {
    version: 'v1.0.10',
    date: 'May 2026',
    headline: 'Knowledge review page in dashboard',
    body: 'Paid-plan users can now see and delete everything Salifly has auto-learned from their sent replies. The new Knowledge page in the web dashboard lists all stored business facts and tone examples, grouped by type, with a relative timestamp and a delete button for each entry.',
    tags: [{ label: 'Feature', kind: 'feature' }],
    items: [
      'New "Knowledge" page in the web dashboard sidebar',
      'Lists all business_fact and tone_example rows from your knowledge base',
      'Per-row delete with optimistic UI and rollback on failure',
      'Embedding column is never surfaced — only human-readable content is shown',
    ],
  },
  {
    version: 'v1.0.9',
    date: 'May 2026',
    headline: 'Inline suggestions in iframe-hosted editors',
    body: 'Inline suggestion underlines and hover cards now work correctly in iframe-hosted editor surfaces — including Salesforce Lightning compose fields and other CRMs that render their compose UI inside an iframe. The hover card positioning now accounts for the parent-frame scroll offset.',
    tags: [{ label: 'Fix', kind: 'fix' }],
    items: [
      'Hover card renders correctly over iframe-hosted suggestion underlines',
      'Frame offset walk handles nested same-origin iframes',
      'Parent-frame scroll offset tracked so cards don\'t drift on scroll',
    ],
  },
  {
    version: 'v1.0.8',
    date: 'May 2026',
    headline: 'Robust inline suggestion widget for CRM editors',
    body: 'Major stability pass on the inline suggestion engine for complex editor environments. Shadow DOM traversal, composed-path event resolution, and frame-level duplicate prevention all land in this release. The badge-visibility check now correctly treats CRM editor chrome as a container rather than a covering overlay.',
    tags: [{ label: 'Fix', kind: 'fix' }],
    items: [
      'Shadow DOM traversal for suggestion detection and rendering',
      'composedPath() / deepActiveElement resolution for shadow-root inputs',
      'Per-frame data-salifly-engine stamp prevents duplicate badges in multi-frame editors',
      'isFieldCoveredByOverlay treats composed ancestors as containers, not blockers',
      'execCommand("insertText") with InputEvent fallback for framework-bound editors',
    ],
  },
];

const TAG_CLASS: Record<TagKind, string> = {
  feature:     'cl-tag cl-tag--feature',
  improvement: 'cl-tag cl-tag--improvement',
  fix:         'cl-tag cl-tag--fix',
  change:      'cl-tag cl-tag--change',
};

export function ChangelogPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: ChangelogPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-wrap">
      <Nav currentPage="changelog" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      <div className="page-hero">
        <span className="page-hero-eyebrow">What's new</span>
        <h1 className="page-hero-h1">
          Changelog
        </h1>
        <p className="page-hero-sub">
          Every change, in order, with enough detail to know what actually shipped.
        </p>
      </div>

      <div className="page-section" style={{ paddingTop: 8, paddingBottom: 48 }}>
        <div className="cl-entries">
          {ENTRIES.map((entry, i) => (
            <div key={entry.version} className={`cl-entry reveal reveal-delay-${(i % 3) + 1}`}>
              <div className="cl-entry-aside">
                <div className="cl-entry-version-wrap">
                  <span className="cl-entry-version">{entry.version}</span>
                </div>
                {i < ENTRIES.length - 1 && <div className="cl-entry-connector" />}
              </div>
              <div className="cl-entry-card">
                <div className="cl-entry-meta">
                  <span className="cl-entry-date">{entry.date}</span>
                  <div className="cl-entry-tags">
                    {entry.tags.map(t => (
                      <span key={t.label} className={TAG_CLASS[t.kind]}>{t.label}</span>
                    ))}
                  </div>
                </div>
                <h3 className="cl-entry-h3">{entry.headline}</h3>
                <p className="cl-entry-body">{entry.body}</p>
                <ul className="cl-entry-items">
                  {entry.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-footer-cta">
        <h2>Ready to try it?</h2>
        <p>Install free. No credit card required.</p>
        <div className="page-footer-cta-actions">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('features')}>See all features</button>
        </div>
      </div>

      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <a href="/privacy" className="page-footer-link">Privacy</a>
              <a href="/terms" className="page-footer-link">Terms</a>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
